import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import { Flame } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      if (u.is_coach) {
        navigate("/coach-dashboard");
      } else {
        navigate(u.onboarded ? "/dashboard" : "/onboarding");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian-900 flex flex-col">
      <header className="border-b border-obsidian-600 px-6 py-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Ripple Effect" className="h-6 w-6 rounded-sm" />
          <span className="font-heading text-xl">RIPPLE<span className="text-blaze"> EFFECT</span></span>
        </Link>
      </header>
      <main className="flex-1 grid place-items-center px-5 py-10">
        <form onSubmit={submit} className="w-full max-w-md obs-card">
          <div className="overline text-blaze mb-2">/ Sign in</div>
          <h1 className="font-heading text-4xl uppercase mb-6">Welcome back.</h1>

          <label className="block mb-4">
            <div className="overline mb-1">Email</div>
            <input
              data-testid={AUTH.loginEmail}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
            />
          </label>
          <label className="block mb-6">
            <div className="overline mb-1">Password</div>
            <input
              data-testid={AUTH.loginPassword}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
            />
          </label>
          <button
            data-testid={AUTH.loginSubmit}
            disabled={loading}
            className="blaze-btn w-full py-3 text-sm disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>

          <GoogleSignInButton label="Sign in with Google" />

          <div className="mt-4 text-center">
            <Link
              to="/auth/forgot-password"
              data-testid="login-forgot-password-link"
              className="text-xs text-zinc-400 hover:text-blaze uppercase tracking-widest"
            >
              Forgot password?
            </Link>
          </div>

          <div className="mt-6 text-sm text-zinc-400 text-center">
            New here?{" "}
            <Link to="/auth/signup" data-testid={AUTH.toSignup} className="text-blaze hover:text-blaze-hover">Create an account</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
